import { AppearanceError } from './errors.js';

/*
 * The app's tabs (docs/26 §4): which show, in what order, their names and icons, at most five. Account must always be
 * reachable (Apple requires account deletion to be): when it isn't a tab, the header's account button is always on.
 * Catering only shows while the catering switch is on. Every tab is drawn alike (the raised centre disc went on 21 Oct).
 */

export const TAB_IDS = ['home', 'menu', 'card', 'offers', 'account', 'catering'] as const;
export type TabId = (typeof TAB_IDS)[number];

/** Where each tab goes in the app. */
/** Where each tab goes in the app (card and offers open Home, where the card and the Rewards sheet live now). */
export const TAB_ROUTES: Record<TabId, string> = { home: '/', menu: '/menu', card: '/card', offers: '/offers', account: '/account', catering: '/catering' };

/** The icons a tab may use: the app's own drawings (TAB_ICON_SVG), so the admin's preview shows the same ones. */
export const TAB_ICONS = ['home', 'bag', 'qr', 'card', 'tag', 'gift', 'rewards', 'star', 'spark', 'heart', 'user', 'people', 'wallet', 'game'] as const;
export type TabIcon = (typeof TAB_ICONS)[number];
export const TAB_ICON_NAMES: Record<TabIcon, string> = {
  home: 'House', bag: 'Bag', qr: 'QR code', card: 'Card', tag: 'Tag', gift: 'Gift', rewards: 'Voucher', star: 'Star',
  spark: 'Sparkle', heart: 'Heart', user: 'Person', people: 'People', wallet: 'Wallet', game: 'Puzzle',
};

export interface TabSetting { id: TabId; enabled: boolean; label: string; icon: TabIcon }
export interface TabsSetting {
  /** Every tab once, in order; only the ones switched on show. */
  tabs: TabSetting[];
  /** The account button in the header (the Home avatar on a phone, the bar's avatar on a laptop). Always on when Account isn't a tab. */
  accountButton: boolean;
}

export const MAX_TABS = 5;
export const MIN_TABS = 2;
export const TAB_LABEL_MAX = 12;

/**
 * The starting tabs (Saif, 21 Oct 2026 evening): Home · Order · Account. Loyalty and Offers folded into Home (the card,
 * the Rewards and Earn sheets); their ids stay valid, so a saved five-tab layout still works and the admin can add them
 * back, and each of them opens Home. The names are the starting ones only.
 */
export const DEFAULT_TABS: TabsSetting = {
  tabs: [
    { id: 'home', enabled: true, label: 'Home', icon: 'home' },
    { id: 'menu', enabled: true, label: 'Order', icon: 'bag' },
    { id: 'card', enabled: false, label: 'Loyalty', icon: 'star' },
    { id: 'offers', enabled: false, label: 'Offers', icon: 'tag' },
    { id: 'account', enabled: true, label: 'Account', icon: 'user' },
    { id: 'catering', enabled: false, label: 'Catering', icon: 'people' },
  ],
  accountButton: true,
};
export const TAB_DEFAULT_LABEL = Object.fromEntries(DEFAULT_TABS.tabs.map((t) => [t.id, t.label])) as Record<TabId, string>;

/** The tabs to save, checked: every tab once, 2–5 on, a name of 1–12 characters, an icon from the list, Account reachable. */
export function checkTabs(raw: unknown): TabsSetting {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  if (!Array.isArray(o.tabs) || o.tabs.length !== TAB_IDS.length) throw new AppearanceError(`List all ${TAB_IDS.length} tabs, each once`, 'bad_tabs');
  const seen = new Set<string>();
  const tabs: TabSetting[] = [];
  for (const t of o.tabs as unknown[]) {
    const r = t && typeof t === 'object' ? (t as Record<string, unknown>) : {};
    const id = r.id as TabId;
    if (!(TAB_IDS as readonly unknown[]).includes(id) || seen.has(id)) throw new AppearanceError(`List all ${TAB_IDS.length} tabs, each once`, 'bad_tabs');
    seen.add(id);
    if (typeof r.enabled !== 'boolean') throw new AppearanceError('Each tab is on or off', 'bad_tabs');
    const label = typeof r.label === 'string' ? r.label.trim() : '';
    if (!label || label.length > TAB_LABEL_MAX) throw new AppearanceError(`Give the ${TAB_DEFAULT_LABEL[id]} tab a name of 1 to ${TAB_LABEL_MAX} characters`, 'bad_tabs');
    if (!(TAB_ICONS as readonly unknown[]).includes(r.icon)) throw new AppearanceError(`Pick an icon for the ${label} tab`, 'bad_tabs');
    tabs.push({ id, enabled: r.enabled, label, icon: r.icon as TabIcon });
  }
  const on = tabs.filter((t) => t.enabled).length;
  if (on > MAX_TABS) throw new AppearanceError(`At most ${MAX_TABS} tabs fit on a phone: switch one off first`, 'bad_tabs');
  if (on < MIN_TABS) throw new AppearanceError(`Keep at least ${MIN_TABS} tabs on`, 'bad_tabs');
  const accountTab = tabs.some((t) => t.id === 'account' && t.enabled);
  if (o.accountButton !== undefined && typeof o.accountButton !== 'boolean') throw new AppearanceError('The account button is on or off', 'bad_tabs');
  if (!accountTab && o.accountButton === false) throw new AppearanceError('Account has to stay reachable (Apple requires account deletion to be): keep the Account tab or the account button', 'account_unreachable');
  return { tabs, accountButton: accountTab ? o.accountButton !== false : true };
}

/** Stored tabs, read leniently: unknown ones dropped, a missing one added at the end (off, unless it's Account), the rules kept. */
export function storedTabs(raw: unknown): TabsSetting {
  try {
    return checkTabs(raw);
  } catch {
    const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const list = Array.isArray(o.tabs) ? (o.tabs as unknown[]) : [];
    const out: TabSetting[] = [];
    for (const t of list) {
      const r = t && typeof t === 'object' ? (t as Record<string, unknown>) : {};
      const def = DEFAULT_TABS.tabs.find((d) => d.id === r.id);
      if (!def || out.some((x) => x.id === def.id)) continue;
      const label = typeof r.label === 'string' && r.label.trim() && r.label.trim().length <= TAB_LABEL_MAX ? r.label.trim() : def.label;
      const icon = (TAB_ICONS as readonly unknown[]).includes(r.icon) ? (r.icon as TabIcon) : def.icon;
      out.push({ id: def.id, enabled: typeof r.enabled === 'boolean' ? r.enabled : def.enabled, label, icon });
    }
    if (!out.length) return DEFAULT_TABS;
    for (const d of DEFAULT_TABS.tabs) if (!out.some((x) => x.id === d.id)) out.push({ ...d, enabled: d.id === 'account' });
    // Too many on: the last ones go off; too few: the starting ones come back.
    let on = 0;
    for (const t of out) if (t.enabled && ++on > MAX_TABS) t.enabled = false;
    if (on < MIN_TABS) return DEFAULT_TABS;
    const accountTab = out.some((t) => t.id === 'account' && t.enabled);
    return { tabs: out, accountButton: accountTab ? o.accountButton !== false : true };
  }
}
